import { isPlatformBrowser } from '@angular/common';
import {
  ApplicationRef,
  ErrorHandler,
  Inject,
  Injectable,
  PLATFORM_ID,
} from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/messaging';
import { SwPush } from '@angular/service-worker';
import {
  LocalNotificationActionPerformed,
  NotificationChannel,
  PermissionType,
  PushNotification,
} from '@capacitor/core';
import { Auth } from '@freelancer/auth';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { LocalStorage } from '@freelancer/local-storage';
import { Location } from '@freelancer/location';
import { Pwa } from '@freelancer/pwa';
import { Tracking } from '@freelancer/tracking';
import { toNumber } from '@freelancer/utils';
import * as Rx from 'rxjs';
import {
  filter,
  first,
  map,
  mapTo,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

export type NotificationPermission = 'granted' | 'denied' | 'default';

export type NotificationRegisterResult =
  | {
      readonly status: 'success';
    }
  | {
      readonly status: 'error';
      readonly errorCode: NotificationPermissionErrorCode;
      // T249142: Required to capture what the problem we're having with
      // some users not registering push notifications correctly.
      readonly errorMsg?: string;
    };

export enum NotificationPermissionErrorCode {
  //  Permission has been denied by the user
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  TOKEN_REQUEST_ERROR = 'TOKEN_REQUEST_ERROR',
}

const TRACK_DELIVERED_NOTIF_EVENT_NAME = 'delivered_notification';
const TRACK_NATIVE_PUSH_NOTIF_SECTION_NAME = 'native_push_notifications';

@Injectable({ providedIn: 'root' })
export class Notifications {
  tokenChanges$: Rx.Observable<string | undefined>;

  private subscriptions = new Rx.Subscription();
  private isRegistered = false;

  constructor(
    private afMessaging: AngularFireMessaging,
    private auth: Auth,
    private appRef: ApplicationRef,
    private errorHandler: ErrorHandler,
    private freelancerHttp: FreelancerHttp,
    private localStorage: LocalStorage,
    private location: Location,
    private push: SwPush,
    private pwa: Pwa,
    private tracking: Tracking,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /**
   * Can permission to show notifications be requested. This implies that
   * permission has not been already granded.
   *
   * Used that to display a user prompt to enable notifications
   */
  async canRequestPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }
    const permission = await this.getPermission();
    return permission === 'default';
  }

  /**
   * Has permission to show notifications already been granted
   *
   * Used to automatically register push notifications on login
   */
  async hasGrantedPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }
    const permission = await this.getPermission();
    return permission === 'granted';
  }

  /**
   * Are notifications supported by the platform.
   *
   * If this is true but both canRequestPermission() are hasGrantedPermission()
   * are false, this implies the user has previously denied permission: use
   * that a display a user prompt to try to recover the user.
   */
  isSupported(): boolean {
    // Disable on the server
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    // Notifications are always supported on native
    if (this.pwa.isNative()) {
      return true;
    }

    // In the browser, both Service Workers & the Push API need to be supported
    // Technically we don't use the Angular Service Worker but using
    // `SwPush::isEnabled` here allows to share the SW enabling logic.
    return (
      this.push.isEnabled && 'PushManager' in window && 'Notification' in window
    );
  }

  /**
   * Register the application to receive push notifications. Will request
   * permission to the user if needed, i.e. do not call that on load unless
   * permission has already been granted.
   */
  async registerPushNotifications(): Promise<NotificationRegisterResult> {
    if (!this.isSupported()) {
      throw new Error(
        'Push notifications are not supported on the current platform: make sure to check isSupported() before calling registerPushNotifications()',
      );
    }

    if (!this.auth.isLoggedIn()) {
      throw new Error('User must be logged-in to register push notifications');
    }

    if (
      !(await this.hasGrantedPermission()) &&
      !(await this.canRequestPermission())
    ) {
      return {
        status: 'error',
        errorCode: NotificationPermissionErrorCode.PERMISSION_DENIED,
      };
    }

    // Do nothing if already registered
    if (this.isRegistered) {
      return {
        status: 'success',
      };
    }

    let foregroundMessages$: Rx.Observable<object>;
    // Foreground messages stream specifically for native.
    let nativeForegroundMessages$: Rx.Observable<PushNotification> | undefined;
    let notificationClicks$: Rx.Observable<{
      action?: string;
      notification: {
        title?: string;
        subtitle?: string;
        body?: string;
        badge?: string;
        data?: {
          url?: string;
        };
      };
    }>;
    if (this.pwa.isNative()) {
      const {
        PushNotifications,
        LocalNotifications,
      } = await this.pwa.capacitorPlugins();

      // Notification channel is for Android only. Android notifications have to
      // be bound to a channel in order for it to display with alert/toast style
      // and other configurations including lights/vibration.
      if (this.pwa.getPlatform() === 'android') {
        const newMessageChannel: NotificationChannel = {
          // This channel ID is also used as the default channel for firebase
          // messaging notifications, set via
          // `com.google.firebase.messaging.default_notification_channel_id`
          id: 'newMessageChannel',
          name: $localize`New Message`,
          description: $localize`New incoming messages`,
          importance: 5,
          sound: 'message.wav',
          visibility: 1,
          vibration: true,
          lights: true,
          lightColor: '#29B2FE',
        };
        PushNotifications.createChannel(newMessageChannel);
        LocalNotifications.createChannel(newMessageChannel);
      }

      // We need to use a replay subject here to cache the registration result until tokenChanges$ is subcribed to
      const registrationSubject$ = new Rx.ReplaySubject<string | undefined>();
      PushNotifications.addListener('registration', t =>
        registrationSubject$.next(t.value),
      );
      PushNotifications.addListener('registrationError', t =>
        registrationSubject$.error(t.data),
      );

      this.tokenChanges$ = Rx.from(
        PushNotifications.requestPermission()
          .then(res => {
            if (res.granted) {
              return PushNotifications.register();
            }
            registrationSubject$.error(
              new Error('Permission denied by user after being granted'),
            );
          })
          .catch(() =>
            registrationSubject$.error(
              new Error('Permission denied by user after being granted'),
            ),
          ),
      ).pipe(switchMap(() => registrationSubject$.asObservable()));

      this.removeNotificationsOnActiveState();

      nativeForegroundMessages$ = new Rx.Observable(observer => {
        const listener = PushNotifications.addListener(
          'pushNotificationReceived',
          notification => observer.next(notification),
        );
        return () => listener.remove();
      });
      foregroundMessages$ = nativeForegroundMessages$;

      notificationClicks$ = new Rx.Observable(observer => {
        const pushNotificationsListener = PushNotifications.addListener(
          'pushNotificationActionPerformed',
          message =>
            observer.next({
              action: message.actionId,
              notification: {
                ...message.notification,
                badge: message.notification.badge?.toString(),
                title: message.notification.title,
              },
            }),
        );

        const localNotificationsListener = LocalNotifications.addListener(
          'localNotificationActionPerformed',
          (actionPerformed: LocalNotificationActionPerformed) => {
            observer.next({
              action: actionPerformed.actionId,
              notification: {
                ...actionPerformed.notification,
                title: actionPerformed.notification.title,
                data: actionPerformed.notification.extra.data,
              },
            });
          },
        );

        return () => {
          pushNotificationsListener.remove();
          localNotificationsListener.remove();
        };
      });

      // Clear the notifications when the user lands on the inbox.
      this.subscriptions.add(
        this.location
          .valueChanges()
          .pipe(filter(location => location.pathname.startsWith('/messages')))
          .subscribe(_ => {
            this.removeNotificationsOnActiveState();
          }),
      );
    } else {
      this.tokenChanges$ = this.afMessaging.requestToken.pipe(
        map(token => token ?? undefined),
      );

      foregroundMessages$ = this.afMessaging.messages;

      notificationClicks$ = new Rx.Observable(observer => {
        const handler = (event: any) => {
          const internalPayload = event.data;

          if (!internalPayload.firebaseMessaging) {
            return;
          }

          if (
            internalPayload.firebaseMessaging.type === 'notification-clicked' &&
            internalPayload.firebaseMessaging.payload
          ) {
            observer.next({
              // TODO: implement custom actions when needed
              notification: {
                ...internalPayload.firebaseMessaging.payload.notification,
                data: internalPayload.firebaseMessaging.payload.data,
              },
            });
          }
        };
        navigator.serviceWorker.addEventListener('message', handler);
        return () => {
          navigator.serviceWorker.removeEventListener('message', handler);
        };
      });
    }

    const result = await this.tokenChanges$
      .pipe(take(1))
      .toPromise()
      .then(() => ({
        status: 'success' as const,
      }))
      .catch(e => ({
        status: 'error' as const,
        errorCode: NotificationPermissionErrorCode.TOKEN_REQUEST_ERROR,
        // T249142: Required to capture what the problem we're having with
        // some users not registering push notifications correctly.
        errorMsg: e.toString(),
      }));

    // Permission has been granted by the user
    if (result.status === 'success') {
      const deviceId = await this.getDeviceId();

      this.isRegistered = true;

      this.subscriptions.add(
        this.tokenChanges$
          .pipe(
            tap(token => console.log('Device token:', token)),
            switchMap(token =>
              this.auth.getUserId().pipe(
                take(1),
                switchMap(userId =>
                  // The token is automatically updated based on the unique device ID
                  this.freelancerHttp.post('users/0.1/tokens', {
                    kind: 'fcm',
                    app_type: 'freelancer',
                    data: token,
                    device_id: deviceId,
                    user_id: toNumber(userId),
                  }),
                ),
              ),
            ),
          )
          .subscribe({
            error: err => {
              this.errorHandler.handleError(err);
            },
          }),
      );

      this.subscriptions.add(
        foregroundMessages$.subscribe(notification => {
          // This is called when notifications are received while the app is in
          // foreground. Presumably we don't need to do anything here for
          // non-native environment as the same notifications will be received
          // through the WebSocket, which will be used to update the UI(pops up
          // the messaging thread) or show a toast notification.
          console.log('New foreground message: ', notification);
        }),
      );

      // New messages that comes through WebSocket doesn't pops up in the UI on
      // Android and iOS. Hence, we create local notifications to alert user.
      if (this.pwa.isNative() && nativeForegroundMessages$) {
        this.subscriptions.add(
          nativeForegroundMessages$.subscribe(async notification => {
            if (notification.body && notification.data.url) {
              // Strip the query params from the relative URL in the notification.
              const redirectURL = notification.data.url.replace(/\?.*$/, '');
              // Ignore notification if we are already in the message thread.
              if (redirectURL !== this.location.pathname) {
                const {
                  LocalNotifications,
                } = await this.pwa.capacitorPlugins();
                // Forward the push notification to local notification.
                LocalNotifications.schedule({
                  notifications: [
                    {
                      // Generate a random ID.
                      id: Math.floor((Math.random() + 1) * 0x10000000),
                      title: notification.title ?? $localize`New Message`,
                      body: notification.body,
                      // This set the sound file for iOS.
                      // Android sound file is set via the channel.
                      sound: 'message.wav',
                      channelId: 'newMessageChannel',
                      extra: {
                        data: notification.data,
                      },
                    },
                  ],
                });
              }
            }
          }),
        );
      }

      this.subscriptions.add(
        notificationClicks$.subscribe(({ action, notification }) => {
          // Track notifications that have been clicked as `trackDeliveredNotifications()`
          // does not do this.
          this.tracking.trackCustomEvent(
            TRACK_DELIVERED_NOTIF_EVENT_NAME,
            TRACK_NATIVE_PUSH_NOTIF_SECTION_NAME,
          );

          // This is called when the user performs an action on a notification
          console.log('Notification click: ', action, notification);
          // If the url data field is set, navigate to the destination
          const url = notification?.data?.url;
          if (url) {
            this.appRef.isStable
              .pipe(first(isStable => isStable))
              .toPromise()
              .then(() => this.location.navigateByUrl(url));
          }
        }),
      );
    }

    return result;
  }

  /**
   * Unregister the application to receive push notifications.
   */
  unregisterPushNotifications(): Promise<void> {
    // Reset subscriptions
    this.subscriptions.unsubscribe();
    this.subscriptions = new Rx.Subscription();
    this.isRegistered = false;

    // Silently return if notifications haven't been setup
    if (!this.tokenChanges$) {
      return Promise.resolve();
    }

    return this.tokenChanges$
      .pipe(
        switchMap(token => {
          if (token) {
            return Rx.combineLatest([
              this.freelancerHttp.delete('users/0.1/tokens', {
                params: {
                  kind: 'fcm',
                  app_type: 'freelancer',
                  data: token,
                },
              }),
              // if not native, delete token
              !this.pwa.isNative()
                ? this.afMessaging.deleteToken(token)
                : // otherwise ignore
                  Rx.of(undefined),
            ]);
          }
          return Rx.EMPTY;
        }),
        take(1),
        mapTo(undefined),
      )
      .toPromise();
  }

  /**
   * @deprecated
   * Manually trigger a notification
   */
  showNotification() {
    // We explicitly DO NOT want to implement that, i.e. create & show
    // Notifications from the webapp itself: all notifications should be
    // transformed/filtered/sent from the backend and are automatically handled
    // in the Service Worker/native background tasks in order to ensure
    // consistency & provide background notifications support accross platforms
    throw Error('Not implemented on purpose');
  }

  /**
   * Remove notifications when user switches back to the app
   */
  async removeNotificationsOnActiveState(): Promise<void> {
    if (this.pwa.isNative()) {
      const { App, PushNotifications } = await this.pwa.capacitorPlugins();

      // Clear notifications on fresh open of the app
      await App.getState().then(async state => {
        if (state.isActive) {
          await this.trackDeliveredNotifications();
          PushNotifications.removeAllDeliveredNotifications();
        }
      });

      // Clear notifications everytime user switches back to the app
      App.addListener('appStateChange', async state => {
        if (state.isActive) {
          await this.trackDeliveredNotifications();
          PushNotifications.removeAllDeliveredNotifications();
        }
      });
    }
  }

  private async trackDeliveredNotifications() {
    const { PushNotifications } = await this.pwa.capacitorPlugins();
    const deliveredNotifications = await PushNotifications.getDeliveredNotifications();
    deliveredNotifications.notifications.forEach(notification => {
      // Filter out the extra `group_summary` notification that is returned
      // here but is not actually a delivered push notification.
      if (!notification.groupSummary) {
        this.tracking.trackCustomEvent(
          TRACK_DELIVERED_NOTIF_EVENT_NAME,
          TRACK_NATIVE_PUSH_NOTIF_SECTION_NAME,
        );
      }
    });
  }

  private async getPermission(): Promise<NotificationPermission> {
    if (this.pwa.isNative()) {
      const { Permissions } = await this.pwa.capacitorPlugins();
      const result = await Permissions.query({
        name: PermissionType.Notifications,
      });
      if (result.state === 'prompt') {
        return 'default';
      }
      return result.state as NotificationPermission;
    }
    return Notification.permission;
  }

  private async getDeviceId(): Promise<string> {
    const deviceId = this.localStorage
      .get('deviceId')
      .pipe(take(1))
      .toPromise();
    if (typeof deviceId === 'string' && deviceId) {
      return deviceId;
    }
    const id = uuidv4();
    await this.localStorage.set('deviceId', id);
    return id;
  }
}
